import {
  authApi,
  BascAlert,
  BascBox,
  BascButton,
  BascCard,
  BascCenter,
  BascFormErrorSummary,
  BascGroup,
  BascLanguageSelector,
  BascPasswordInput,
  BascStack,
  BascText,
  BascTextInput,
  useTranslation,
} from '@bco/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { loadClientAssets } from '../../federationInit';
import { buildLoginBackgroundStyle } from '../../utils/loginBackgroundStyle';
import './forgot-password.css';

interface ClientAssets {
  appLogo?: string;
  appLogoDark?: string;
  loginBackground?: string;
}

type Step = 'request' | 'confirm' | 'success';

export function ForgotPasswordPage() {
  const { clientName = '' } = useParams<{ clientName: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState(emailFromQuery);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [assets, setAssets] = useState<ClientAssets | null>(null);

  // Load client assets on mount
  useEffect(() => {
    async function loadAssets() {
      const clientAssets = await loadClientAssets(clientName);
      setAssets(clientAssets);
    }
    if (clientName) {
      loadAssets();
    }
  }, [clientName]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authApi.forgotPassword(clientName, email);
      setCodeSent(true);
    } catch (err: any) {
      setError(err.message || t('errors.sendResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || !newPassword || !confirmPassword) {
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('validation.passwordMinLength', { min: 8 }));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authApi.resetPassword(clientName, email, code, newPassword);
      setStep('success');
    } catch (err: any) {
      setError(err.message || t('errors.resetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const backgroundStyle = buildLoginBackgroundStyle(assets);

  return (
    <BascBox className="forgot-password-page" style={backgroundStyle}>
      <BascBox className="forgot-password-overlay" />
      <BascCenter className="forgot-password-container">
        <BascCard
          className="forgot-password-card"
          shadow="xl"
          padding="xl"
          radius="md"
        >
          <BascStack gap="lg">
            {/* Logo */}
            {assets?.appLogo && (
              <BascGroup justify="center">
                <BrandLogo
                  src={assets.appLogo}
                  srcDark={assets.appLogoDark}
                  alt="BCO PORTAL"
                  className="forgot-password-logo"
                />
              </BascGroup>
            )}

            {/* Step 1: Request Reset */}
            {step === 'request' && (
              <form onSubmit={handleRequestReset}>
                <BascStack gap="md">
                  <BascText
                    className="forgot-password-title"
                    ta="center"
                    textRole="heading1"
                    fw={600}
                  >
                    {t('auth.forgotPasswordTitle')}
                  </BascText>
                  <BascText
                    className="forgot-password-subtitle"
                    ta="center"
                    textRole="body"
                  >
                    {t('auth.codeSentDesc')}
                  </BascText>

                  <BascFormErrorSummary
                    errors={error ? [{ message: error }] : []}
                  />

                  {codeSent && (
                    <BascAlert
                      className="forgot-password-success"
                      color="green"
                      variant="light"
                    >
                      {t('auth.codeSentSuccess')}
                    </BascAlert>
                  )}

                  <BascTextInput
                    label={t('auth.email')}
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    required
                    type="email"
                    autoComplete="email"
                    disabled={isLoading || !!emailFromQuery}
                    readOnly={!!emailFromQuery}
                  />

                  <BascButton
                    emphasis="primary"
                    type="submit"
                    fullWidth
                    loading={isLoading}
                    disabled={!email}
                    className="forgot-password-submit"
                  >
                    {codeSent ? t('auth.resendCode') : t('auth.sendResetCode')}
                  </BascButton>

                  {codeSent && (
                    <BascButton
                      variant="light"
                      fullWidth
                      onClick={() => setStep('confirm')}
                    >
                      {t('auth.iHaveCode')}
                    </BascButton>
                  )}
                </BascStack>
              </form>
            )}

            {/* Step 2: Confirm Reset */}
            {step === 'confirm' && (
              <form onSubmit={handleConfirmReset}>
                <BascStack gap="md">
                  <BascText
                    className="forgot-password-title"
                    ta="center"
                    textRole="heading1"
                    fw={600}
                  >
                    {t('auth.resetPassword')}
                  </BascText>
                  <BascText
                    className="forgot-password-subtitle"
                    ta="center"
                    textRole="body"
                  >
                    {t('auth.resetPasswordDesc', { email })}
                  </BascText>

                  <BascFormErrorSummary
                    errors={error ? [{ message: error }] : []}
                  />

                  <BascTextInput
                    id="forgot-password-code"
                    label={t('auth.verificationCode')}
                    placeholder={t('auth.enterCode')}
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value)}
                    required
                    disabled={isLoading}
                  />

                  <BascPasswordInput
                    id="forgot-password-new"
                    label={t('auth.newPassword')}
                    placeholder={t('auth.enterNewPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.currentTarget.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                    description={t('register.passwordRequirements')}
                  />

                  <BascPasswordInput
                    id="forgot-password-confirm"
                    label={t('auth.confirmPassword')}
                    placeholder={t('auth.confirmNewPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                  />

                  <BascButton
                    emphasis="primary"
                    type="submit"
                    fullWidth
                    loading={isLoading}
                    disabled={!code || !newPassword || !confirmPassword}
                    className="forgot-password-submit"
                  >
                    {t('auth.resetPassword')}
                  </BascButton>

                  <BascButton
                    variant="subtle"
                    fullWidth
                    onClick={() => setStep('request')}
                    disabled={isLoading}
                  >
                    {t('auth.backToRequestCode')}
                  </BascButton>
                </BascStack>
              </form>
            )}

            {/* Step 3: Success */}
            {step === 'success' && (
              <BascStack gap="md">
                <BascText
                  className="forgot-password-title"
                  ta="center"
                  textRole="heading1"
                  fw={600}
                >
                  {t('auth.passwordResetSuccess')}
                </BascText>

                <BascAlert
                  className="forgot-password-success"
                  color="green"
                  variant="light"
                >
                  {t('auth.passwordResetSuccessDesc')}
                </BascAlert>

                <BascButton
                  emphasis="primary"
                  fullWidth
                  onClick={() => navigate(`/${clientName}/login`)}
                  className="forgot-password-submit"
                >
                  {t('goToLogin')}
                </BascButton>
              </BascStack>
            )}

            {/* Back to Login Link (only on request step) */}
            {step === 'request' && (
              <BascText
                className="forgot-password-back-link"
                ta="center"
                textRole="body"
              >
                {t('auth.rememberPassword')}{' '}
                <a
                  href={`/${clientName}/login`}
                  className="forgot-password-link"
                >
                  {t('auth.signIn')}
                </a>
              </BascText>
            )}

            {/* Language Selector */}
            <BascGroup justify="center">
              <BascLanguageSelector compact label={t('language.change')} />
            </BascGroup>
          </BascStack>
        </BascCard>
      </BascCenter>
    </BascBox>
  );
}

export default ForgotPasswordPage;
